<template>
  <div class="sales-page">
    <header class="sales-page__header">
      <div>
        <h1>{{ $t('sales.title') }}</h1>
        <p>{{ $t('sales.subtitle') }}</p>
      </div>

      <div class="sales-page__header-tools">
        <v-text-field
          v-model="search"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('sales.searchPlaceholder')"
          class="sales-page__search"
        />
        <div v-if="totalSales" class="sales-page__stat">
          <span>{{ $t('sales.total') }}</span>
          <strong>{{ totalSales }}</strong>
        </div>
      </div>
    </header>

    <section class="sales-page__filters">
      <div class="sales-page__filter-row sales-page__filter-row--dates">
        <label class="sales-page__date-field">
          <span>{{ $t('sales.dateFrom') }}</span>
          <button type="button" class="sales-page__date-btn" @click="filterDateTarget = 'from'">
            <i class="mdi mdi-calendar-month-outline"></i>
            <span>{{ filters.date_from ? formatFilterDate(filters.date_from) : $t('sales.selectDate') }}</span>
          </button>
        </label>
        <label class="sales-page__date-field">
          <span>{{ $t('sales.dateTo') }}</span>
          <button type="button" class="sales-page__date-btn" @click="filterDateTarget = 'to'">
            <i class="mdi mdi-calendar-month-outline"></i>
            <span>{{ filters.date_to ? formatFilterDate(filters.date_to) : $t('sales.selectDate') }}</span>
          </button>
        </label>
        <div class="sales-page__date-actions">
          <button
            type="button"
            class="sales-page__yesterday"
            :class="{ 'is-active': isYesterdayRange }"
            @click="setYesterday"
          >
            {{ $t('common.yesterday') }}
          </button>
          <button
            type="button"
            class="sales-page__today"
            :class="{ 'is-active': isTodayRange }"
            @click="setToday"
          >
            {{ $t('common.today') }}
          </button>
          <button
            type="button"
            class="sales-page__all-period"
            :class="{ 'is-active': allPeriod }"
            @click="toggleAllPeriod"
          >
            {{ $t('sales.allPeriod') }}
          </button>
          <button
            v-if="filters.date_from || filters.date_to"
            type="button"
            class="sales-page__clear-dates"
            @click="clearDates"
          >
            {{ $t('sales.clearDates') }}
          </button>
        </div>
      </div>

      <div class="sales-page__filter-row sales-page__filter-row--fields">
        <select v-model="filters.product_id" class="sales-page__select" @change="onProductFilterChange">
          <option value="">{{ $t('sales.allProducts') }}</option>
          <option v-for="product in products" :key="product.id" :value="String(product.id)">
            {{ product.name }}
          </option>
        </select>
        <select v-model="filters.supplier_id" class="sales-page__select">
          <option value="">{{ $t('sales.allSuppliers') }}</option>
          <option v-for="supplier in suppliers" :key="supplier.id" :value="String(supplier.id)">
            {{ supplier.name }}
          </option>
        </select>
        <select v-model="filters.sale_mode" class="sales-page__select">
          <option value="">{{ $t('sales.allModes') }}</option>
          <option value="catalog">{{ $t('sales.catalogMode') }}</option>
          <option value="quick">{{ $t('sales.quick') }}</option>
        </select>
        <div class="sales-page__filter-extra">
          <label class="sales-page__check">
            <input v-model="filters.has_discount" type="checkbox">
            {{ $t('sales.withDiscount') }}
          </label>
          <button type="button" class="sales-page__export" @click="exportCsv">
            <i class="mdi mdi-download-outline"></i> CSV
          </button>
        </div>
      </div>

      <section v-if="selectedProduct" class="sales-page__product-info">
        <div class="sales-page__product-info-head">
          <div>
            <span>{{ $t('sales.selectedProduct') }}</span>
            <strong>{{ selectedProduct.name }}</strong>
          </div>
          <div>
            <span>{{ $t('sales.historyQuantity') }}</span>
            <strong>{{ selectedProductHistoryLabel }}</strong>
          </div>
          <div>
            <span>{{ $t('sales.cost') }}</span>
            <strong>{{ formatMoney(selectedProduct.self_price) }}</strong>
          </div>
          <div>
            <span>{{ $t('sales.supplierDue') }}</span>
            <strong>{{ formatMoney(selectedProductSupplierDue) }}</strong>
          </div>
          <div>
            <span>{{ $t('sales.stockNow') }}</span>
            <strong>{{ selectedProductStockLabel }}</strong>
          </div>
        </div>
      </section>

      <div class="sales-page__pay-chips">
        <span class="sales-page__pay-label">{{ $t('sales.paymentFilter') }}</span>
        <button
          type="button"
          class="sales-page__chip"
          :class="{ 'is-active': !filters.payment_types.length }"
          @click="clearPaymentTypes"
        >
          {{ $t('sales.allPayments') }}
        </button>
        <button
          v-for="type in paymentSums"
          :key="type.value"
          type="button"
          class="sales-page__chip"
          :class="{ 'is-active': isPaymentTypeSelected(type.value) }"
          @click="togglePaymentType(type.value)"
        >
          <span>{{ type.label }}</span>
          <strong>{{ formatMoney(type.amount) }}</strong>
        </button>
      </div>
    </section>

    <div v-if="loading" class="sales-page__loading">
      <i class="mdi mdi-loading mdi-spin"></i>
      <span>{{ $t('common.loading') }}</span>
    </div>

    <template v-else>
    <section class="sales-page__overview">
      <div><span>{{ $t('sales.revenue') }}</span><strong>{{ formatMoney(summary.revenue) }}</strong></div>
      <div><span>{{ $t('sales.receipts') }}</span><strong>{{ summary.receipts || 0 }}</strong></div>
      <div><span>{{ $t('sales.averageReceipt') }}</span><strong>{{ formatMoney(summary.average_receipt) }}</strong></div>
      <div><span>{{ $t('sales.discountsTotal') }}</span><strong>{{ formatMoney(summary.discounts) }}</strong></div>
      <button
        type="button"
        class="sales-page__kpi-toggle"
        :class="{ 'is-off': !profitParts.supplier }"
        :title="profitParts.supplier ? $t('sales.profitPartOn') : $t('sales.profitPartOff')"
        @click="toggleProfitPart('supplier')"
      >
        <span>{{ $t('sales.supplierDue') }}</span>
        <strong>{{ formatMoney(summary.supplier_due) }}</strong>
        <em class="sales-page__overview-hint">{{ $t('sales.supplierDueHint') }}</em>
      </button>
      <button
        type="button"
        class="sales-page__kpi-toggle"
        :class="{ 'is-off': !profitParts.expenses }"
        :title="profitParts.expenses ? $t('sales.profitPartOn') : $t('sales.profitPartOff')"
        @click="toggleProfitPart('expenses')"
      >
        <span>{{ $t('sales.expensesTotal') }}</span>
        <strong>{{ formatMoney(summary.expenses) }}</strong>
      </button>
      <button
        type="button"
        class="sales-page__kpi-toggle"
        :class="{ 'is-off': !profitParts.wastes }"
        :title="profitParts.wastes ? $t('sales.profitPartOn') : $t('sales.profitPartOff')"
        @click="toggleProfitPart('wastes')"
      >
        <span>{{ $t('sales.wastesTotal') }}</span>
        <strong>{{ formatMoney(summary.wastes) }}</strong>
      </button>
      <div class="sales-page__overview-profit">
        <span>{{ $t('sales.profit') }}</span>
        <strong>{{ formatMoney(displayProfit) }}</strong>
      </div>
    </section>

    <section class="sales-page__formula" aria-label="profit formula" @click.stop>
      <div class="sales-page__formula-title">{{ $t('sales.profitFormulaTitle') }}</div>
      <div class="sales-page__formula-line">
        <span class="sales-page__formula-term">
          <em>{{ $t('sales.profit') }}</em>
          <strong :class="{ 'is-neg': displayProfit < 0 }">{{ formatMoney(displayProfit) }}</strong>
        </span>
        <span class="sales-page__formula-eq">=</span>
        <span class="sales-page__formula-term">
          <em>{{ $t('sales.revenue') }}</em>
          <strong>{{ formatMoney(summary.revenue) }}</strong>
        </span>
        <template v-for="part in profitFormulaParts">
          <span :key="`${part.key}-op`" class="sales-page__formula-op" :class="{ 'is-off': !part.enabled }">−</span>
          <button
            :key="part.key"
            type="button"
            class="sales-page__formula-term sales-page__formula-term--btn"
            :class="{ 'is-off': !part.enabled }"
            @click="toggleProfitPart(part.key)"
          >
            <em>{{ part.label }}</em>
            <strong>{{ formatMoney(part.value) }}</strong>
          </button>
        </template>
      </div>
      <p class="sales-page__formula-hint">{{ $t('sales.profitFormulaHint') }}</p>
    </section>

    <section class="sales-page__units">
      <article>
        <span>{{ $t('sales.soldUnits') }}</span>
        <strong>{{ formatUnitTotals(summary.sold_by_unit) }}</strong>
      </article>
      <article>
        <span>{{ $t('sales.addedUnits') }}</span>
        <strong>{{ formatUnitTotals(summary.added_by_unit) }}</strong>
      </article>
    </section>

    <div v-if="sales.length" class="sales-page__list">
      <article v-for="sale in sales" :key="sale.id" class="sale-card">
        <div class="sale-card__head">
          <div class="sale-card__identity">
            <div class="sale-card__icon">
              <i class="mdi mdi-receipt-text-outline"></i>
            </div>
            <div>
              <h3>{{ $t('sales.receipt', { id: sale.id }) }}</h3>
              <time>{{ formatDateTime(sale.created_at) }}</time>
              <span v-if="cashierName(sale)" class="sale-card__cashier">
                <i class="mdi mdi-account-outline"></i>
                {{ cashierName(sale) }}
              </span>
            </div>
          </div>

          <div class="sale-card__summary">
            <div class="sale-card__badges">
              <span
                v-if="Number(sale.discount_total) > 0"
                class="sale-card__badge sale-card__badge--discount"
              >
                {{ $t('sales.discount', { amount: formatMoney(sale.discount_total) }) }}
              </span>
              <span
                v-if="sale.sale_mode === 'quick'"
                class="sale-card__badge sale-card__badge--quick"
              >
                {{ $t('sales.quick') }}
              </span>
              <span class="sale-card__badge">{{ paymentLabel(sale) }}</span>
              <span v-if="sale.status === 'refunded'" class="sale-card__badge sale-card__badge--refunded">{{ $t('sales.refunded') }}</span>
            </div>
            <strong>{{ formatMoney(saleDisplayTotal(sale)) }}</strong>
          </div>
        </div>

        <div v-if="saleItems(sale).length" class="sale-card__table">
          <div class="sale-card__row sale-card__row--head">
            <span>{{ $t('sales.product') }}</span>
            <span>{{ $t('sales.quantity') }}</span>
            <span>{{ $t('sales.price') }}</span>
            <span>{{ $t('sales.amount') }}</span>
          </div>
          <div
            v-for="(item, index) in saleItems(sale)"
          :key="index"
            class="sale-card__item"
            :class="{ 'sale-card__item--alt': index % 2 === 1 }"
          >
            <div class="sale-card__row">
              <span class="sale-card__name">
                {{ itemName(item) }}
                <em v-if="itemHasDiscount(item)" class="sale-card__discount-tag">
                  {{ itemDiscountLabel(item) }}
                </em>
                <small v-if="itemModifiers(item).length" class="sale-card__mods">
                  {{ itemModifiersLabel(item) }}
                </small>
                <span v-if="itemByproducts(item).length" class="sale-card__together">
                  <i class="mdi mdi-link-variant"></i>
                  <em>{{ $t('sales.soldTogether') }}:</em>
                  {{ itemByproductsLabel(item) }}
                </span>
              </span>
              <span>{{ itemQuantityLabel(item) }}</span>
              <span>
                <s v-if="itemHasDiscount(item)" class="sale-card__old-price">
                  {{ formatMoney(itemDisplayBase(item)) }}
                </s>
                {{ formatMoney(itemUnitPrice(item)) }}
              </span>
              <span class="sale-card__line">{{ formatMoney(itemLineTotal(item)) }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="sale.sale_mode === 'quick'" class="sale-card__empty-items sale-card__empty-items--quick">
          <i class="mdi mdi-flash-outline"></i>
          {{ $t('sales.quickSaleEmpty') }}
        </div>
        <div v-else class="sale-card__empty-items">{{ $t('sales.receiptEmpty') }}</div>

        <div v-if="salePayments(sale).length > 1" class="sale-card__payments">
          <span
            v-for="(pay, i) in salePayments(sale)"
            :key="i"
            class="sale-card__pay-chip"
          >
            {{ paymentTypeLabel(pay.payment_type) }}
            · {{ formatMoney(pay.amount) }}
          </span>
        </div>

        <div class="sale-card__actions">
          <button
            type="button"
            class="sale-card__print-btn"
            @click="printSale(sale)"
          >
            <i class="mdi mdi-printer-outline"></i>
            {{ $t('sales.print') }}
          </button>
          <button
            v-if="sale.status !== 'refunded'"
            type="button"
            class="sale-card__refund-btn"
            @click="openRefund(sale)"
          >
            <i class="mdi mdi-undo-variant"></i>
            {{ $t('sales.refund') }}
          </button>
        </div>
      </article>
      </div>

    <div v-else class="sales-page__empty">
      <i class="mdi mdi-cash-register"></i>
      <p>{{ emptyTitle }}</p>
      <span>{{ emptyText }}</span>
    </div>

    <div v-if="pageCount > 1" class="sales-page__pagination">
        <v-pagination
            v-model="page"
            :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
        />
      </div>
    </template>

    <v-dialog
      v-model="refundDialog"
      max-width="420"
      persistent
      @keydown.esc="closeRefundDialog"
    >
      <div v-if="selectedSale" class="sale-refund-modal">
        <header class="sale-refund-modal__header">
          <div>
            <h2>{{ $t('sales.refund') }}</h2>
            <p>{{ $t('sales.receipt', { id: selectedSale.id }) }} · {{ formatMoney(selectedSale.total) }}</p>
    </div>
          <button type="button" class="sale-refund-modal__close" @click="closeRefundDialog">
            <i class="mdi mdi-close"></i>
          </button>
        </header>
        <div class="sale-refund-modal__body">
          <label class="sale-refund-modal__field">
            <span>{{ $t('sales.refundReason') }}</span>
            <textarea
              v-model.trim="refundReason"
              rows="3"
              maxlength="255"
              :placeholder="$t('sales.refundReason')"
            />
          </label>
        </div>
        <footer class="sale-refund-modal__footer">
          <button type="button" class="sale-refund-modal__btn sale-refund-modal__btn--ghost" @click="closeRefundDialog">
            {{ $t('sales.close') }}
          </button>
          <button type="button" class="sale-refund-modal__btn sale-refund-modal__btn--danger" @click="refundSelected">
            {{ $t('sales.refund') }}
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog width="320" v-model="filterDateOpen" content-class="sales-filter-date">
      <div class="sales-date">
        <v-date-picker
          v-model="filterDateDraft"
          :locale="datePickerLocale"
          full-width
          color="#0b4f8a"
          no-title
        />
        <button type="button" class="sales-date__btn" @click="confirmFilterDate">
          {{ $t('sales.selectDateDone') }}
        </button>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { exportSales, getPayments, getSales, getSalesSummary, refundSale } from '@/api/sales'
import { getProducts } from '@/api/products'
import { getSuppliers } from '@/api/suppliers'
import { getCompanyName } from '@/api/auth'
import { tryPrintZywell } from '@/api/printer'
import { formatMoney, getCountTypeLabel } from '@/utils/format'
import { notify, notifyError } from '@/utils/notify'

function todayIso() {
  return isoShift(0)
}

function yesterdayIso() {
  return isoShift(-1)
}

function isoShift(days) {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
  local.setUTCDate(local.getUTCDate() + days)
  return local.toISOString().slice(0, 10)
}

const PROFIT_PARTS_KEY = 'sales_profit_parts_v1'

function loadProfitParts() {
  try {
    const raw = JSON.parse(localStorage.getItem(PROFIT_PARTS_KEY) || 'null')
    if (!raw || typeof raw !== 'object') {
      return { supplier: true, expenses: true, wastes: true }
    }
    return {
      supplier: raw.supplier !== false,
      expenses: raw.expenses !== false,
      wastes: raw.wastes !== false,
    }
  } catch (e) {
    return { supplier: true, expenses: true, wastes: true }
  }
}

export default {
  name: 'SalePage',
  data() {
    return {
      page: 1,
      pageCount: 0,
      totalSales: 0,
      sales: [],
      search: '',
      paymentTypes: [],
      products: [],
      suppliers: [],
      loading: true,
      allPeriod: false,
      savedDateRange: null,
      filters: {
        date_from: todayIso(),
        date_to: todayIso(),
        payment_types: [],
        product_id: '',
        supplier_id: '',
        sale_mode: '',
        has_discount: false,
      },
      filterDateTarget: null,
      filterDateDraft: '',
      profitParts: loadProfitParts(),
      summary: {
        revenue: 0,
        receipts: 0,
        average_receipt: 0,
        discounts: 0,
        supplier_due: 0,
        expenses: 0,
        wastes: 0,
        cost: 0,
        profit: 0,
        sold_by_unit: { count: 0, kg: 0, litr: 0 },
        added_by_unit: { count: 0, kg: 0, litr: 0 },
      },
      refundDialog: false,
      selectedSale: null,
      refundReason: '',
      formatMoney,
    }
  },
  computed: {
    datePickerLocale() {
      return this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU'
    },
    isTodayRange() {
      if (this.allPeriod) return false
      const today = todayIso()
      return this.filters.date_from === today && this.filters.date_to === today
    },
    isYesterdayRange() {
      if (this.allPeriod) return false
      const yesterday = yesterdayIso()
      return this.filters.date_from === yesterday && this.filters.date_to === yesterday
    },
    selectedProduct() {
      const productId = String(this.filters.product_id || '')
      if (!productId) return null
      return this.products.find((product) => String(product.id) === productId) || null
    },
    selectedProductUnit() {
      if (!this.selectedProduct) return this.$t('sales.unitPcs')
      const type = this.selectedProduct.count_type || 'count'
      if (type === 'kg') return this.$t('sales.unitKg')
      if (type === 'litr') return this.$t('sales.unitL')
      return this.$t('sales.unitPcs')
    },
    selectedProductStockLabel() {
      if (!this.selectedProduct) return '—'
      const stock = this.selectedProduct.quantity
      if (stock == null || stock === '') return '—'
      return `${stock} ${this.selectedProductUnit}`
    },
    selectedProductHistoryQty() {
      if (!this.selectedProduct) return 0
      const history = this.selectedProduct.history_quantity
      if (history != null && history !== '') return Number(history) || 0
      return Number(this.selectedProduct.quantity) || 0
    },
    selectedProductHistoryLabel() {
      if (!this.selectedProduct) return '—'
      return `${this.selectedProductHistoryQty} ${this.selectedProductUnit}`
    },
    selectedProductSupplierDue() {
      if (!this.selectedProduct) return 0
      const fromSummary = Number(this.summary.supplier_due)
      if (Number.isFinite(fromSummary) && fromSummary > 0) return fromSummary
      const cost = Number(this.selectedProduct.self_price) || 0
      return Math.round(this.selectedProductHistoryQty * cost * 100) / 100
    },
    filterDateOpen: {
      get() {
        return !!this.filterDateTarget
      },
      set(open) {
        if (!open) this.filterDateTarget = null
      },
    },
    paymentSums() {
      const rows = Array.isArray(this.summary.payments) ? this.summary.payments : []
      const byType = {}
      rows.forEach((row) => {
        byType[row.payment_type] = Number(row.amount) || 0
      })
      const known = new Set(this.paymentTypes.map((type) => type.value))
      const list = this.paymentTypes.map((type) => ({
        value: type.value,
        label: type.label,
        amount: byType[type.value] || 0,
      }))
      rows.forEach((row) => {
        if (known.has(row.payment_type)) return
        list.push({
          value: row.payment_type,
          label: this.paymentTypeLabel(row.payment_type),
          amount: Number(row.amount) || 0,
        })
      })
      return list
    },
    displayProfit() {
      const revenue = Number(this.summary.revenue) || 0
      let cost = 0
      if (this.profitParts.supplier) cost += Number(this.summary.supplier_due) || 0
      if (this.profitParts.expenses) cost += Number(this.summary.expenses) || 0
      if (this.profitParts.wastes) cost += Number(this.summary.wastes) || 0
      return Math.round((revenue - cost) * 100) / 100
    },
    profitFormulaParts() {
      return [
        {
          key: 'supplier',
          label: this.$t('sales.supplierDue'),
          value: Number(this.summary.supplier_due) || 0,
          enabled: this.profitParts.supplier,
        },
        {
          key: 'expenses',
          label: this.$t('sales.expensesTotal'),
          value: Number(this.summary.expenses) || 0,
          enabled: this.profitParts.expenses,
        },
        {
          key: 'wastes',
          label: this.$t('sales.wastesTotal'),
          value: Number(this.summary.wastes) || 0,
          enabled: this.profitParts.wastes,
        },
      ]
    },
    emptyTitle() {
      return this.search ? this.$t('sales.nothingFound') : this.$t('sales.noSales')
    },
    emptyText() {
      return this.search
        ? this.$t('sales.tryAnother')
        : this.$t('sales.receiptsAppear')
    },
  },
  mounted() {
    this.loadPaymentTypes()
    this.loadProducts()
    this.loadSuppliers()
    this.load()
  },
  watch: {
    page() {
      this.load()
    },
    search() {
      this.debouncedSearch()
    },
    filters: {
      handler() { this.resetToFirstPage() },
      deep: true,
    },
    filterDateTarget(target) {
      if (!target) return
      this.filterDateDraft = target === 'from'
        ? (this.filters.date_from || todayIso())
        : (this.filters.date_to || todayIso())
    },
    refundDialog(isOpen) {
      if (!isOpen) {
        this.selectedSale = null
        this.refundReason = ''
      }
    },
  },
  methods: {
    formatFilterDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString(this.datePickerLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },
    setToday() {
      this.leaveAllPeriod()
      const today = todayIso()
      this.filters.date_from = today
      this.filters.date_to = today
    },
    setYesterday() {
      this.leaveAllPeriod()
      const yesterday = yesterdayIso()
      this.filters.date_from = yesterday
      this.filters.date_to = yesterday
    },
    clearDates() {
      this.leaveAllPeriod()
      this.filters.date_from = ''
      this.filters.date_to = ''
    },
    leaveAllPeriod() {
      this.allPeriod = false
      this.savedDateRange = null
    },
    confirmFilterDate() {
      this.leaveAllPeriod()
      const value = this.filterDateDraft || ''
      if (this.filterDateTarget === 'from') {
        this.filters.date_from = value
      } else if (this.filterDateTarget === 'to') {
        this.filters.date_to = value
      }
      this.filterDateTarget = null
    },
    formatUnitTotals(units) {
      const data = units || {}
      const parts = []
      const count = Number(data.count) || 0
      const kg = Number(data.kg) || 0
      const litr = Number(data.litr) || 0
      if (count) parts.push(`${this.formatQty(count)} ${this.$t('sales.unitPcs')}`)
      if (kg) parts.push(`${this.formatQty(kg)} ${this.$t('sales.unitKg')}`)
      if (litr) parts.push(`${this.formatQty(litr)} ${this.$t('sales.unitL')}`)
      return parts.length ? parts.join(' · ') : `0 ${this.$t('sales.unitPcs')}`
    },
    formatQty(value) {
      const amount = Number(value) || 0
      if (Number.isInteger(amount)) return String(amount)
      return String(Math.round(amount * 1000) / 1000)
    },
    formatDateTime(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleString(this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    saleItems(sale) {
      const items = (sale && sale.order_item) || []
      const productId = String(this.filters.product_id || '')
      if (!productId) return items
      return items.filter((item) => String(item.product_id || (item.product && item.product.id) || '') === productId)
    },
    saleDisplayTotal(sale) {
      if (!this.filters.product_id) return Number(sale && sale.total) || 0
      return this.saleItems(sale).reduce((sum, item) => sum + this.itemLineTotal(item), 0)
    },
    onProductFilterChange() {
      if (!this.filters.product_id) {
        if (!this.allPeriod) return
        this.allPeriod = false
        this.restoreDateRange()
        return
      }
      if (this.allPeriod) return
      this.allPeriod = true
      this.onAllPeriodChange()
    },
    toggleAllPeriod() {
      this.allPeriod = !this.allPeriod
      this.onAllPeriodChange()
    },
    onAllPeriodChange() {
      if (this.allPeriod) {
        this.savedDateRange = {
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
        }
        this.filters.date_from = ''
        this.filters.date_to = ''
        return
      }
      this.restoreDateRange()
    },
    restoreDateRange() {
      if (!this.savedDateRange) return
      this.filters.date_from = this.savedDateRange.date_from
      this.filters.date_to = this.savedDateRange.date_to
      this.savedDateRange = null
    },
    salePayments(sale) {
      return (sale && sale.payment) || []
    },
    itemName(item) {
      return (item && item.product && item.product.name) || this.$t('sales.productDeleted')
    },
    itemModifiers(item) {
      return (item && Array.isArray(item.modifiers) ? item.modifiers : []) || []
    },
    itemModifiersLabel(item) {
      return this.itemModifiers(item)
        .map((mod) => `+ ${mod.name}${Number(mod.price) ? ` (${this.formatMoney(mod.price)})` : ''}`)
        .join(', ')
    },
    itemByproducts(item) {
      const list = item && item.product && item.product.byproducts
      return Array.isArray(list) ? list : []
    },
    itemByproductsLabel(item) {
      return this.itemByproducts(item)
        .map((row) => {
          const qty = Number(row.quantity)
          const unit = row.count_type === 'kg'
            ? this.$t('sales.unitKg')
            : this.$t('sales.unitPcs')
          const qtyLabel = Number.isFinite(qty) ? qty : row.quantity
          return `${row.name} ${qtyLabel} ${unit}`
        })
        .join(' · ')
    },
    itemDisplayBase(item) {
      const mods = this.itemModifiers(item).reduce((sum, mod) => sum + (Number(mod.price) || 0), 0)
      return Number((item && item.base_price) || 0) + mods
    },
    cashierName(sale) {
      return (sale && sale.user && (sale.user.name || sale.user.email)) || ''
    },
    itemHasDiscount(item) {
      return Number(item && item.discount_amount) > 0
    },
    itemDiscountLabel(item) {
      const name = (item && item.discount_name) || this.$t('sales.discountDefault')
      if (item && item.discount_type === 'percent') {
        return `${name} −${Number(item.discount_value)}%`
      }
      if (item && item.discount_value != null) {
        return `${name} −${this.formatMoney(item.discount_value)}`
      }
      return name
    },
    itemUnitPrice(item) {
      if (item && item.price != null) return item.price
      return (item && item.product && item.product.price) || 0
    },
    itemLineTotal(item) {
      return Number(this.itemUnitPrice(item) || 0) * Number((item && item.quantity) || 0)
    },
    itemStock(item) {
      if (!item || !item.product || item.product.quantity == null) return null
      return Number(item.product.quantity)
    },
    itemUnitShort(item) {
      const type = (item && item.product && item.product.count_type) || 'count'
      if (type === 'kg') return this.$t('sales.unitKg')
      if (type === 'litr') return this.$t('sales.unitL')
      return this.$t('sales.unitPcs')
    },
    itemQuantityLabel(item) {
      const qty = item && item.quantity != null && item.quantity !== '' ? item.quantity : 0
      return `${qty} ${this.itemUnitShort(item)}`
    },
    itemStockLabel(item) {
      const stock = this.itemStock(item)
      if (stock == null || Number.isNaN(stock)) return '—'
      const unit = getCountTypeLabel((item.product && item.product.count_type) || 'count')
      const shortUnit = unit.replace(/^по\s+/i, '')
      return `${stock} ${shortUnit}`
    },
    itemStockLow(item) {
      const stock = this.itemStock(item)
      return stock != null && !Number.isNaN(stock) && stock <= 5
    },
    itemExpensesAmount(item) {
      const list = item && item.product && item.product.expenses
      if (!Array.isArray(list)) return 0
      return list.reduce((sum, row) => sum + Math.max(0, Number(row.amount) || 0), 0)
    },
    itemWastesQty(item) {
      const list = item && item.product && item.product.wastes
      if (!Array.isArray(list)) return 0
      return list.reduce((sum, row) => sum + Math.max(0, Number(row.quantity) || 0), 0)
    },
    itemProfitBreakdown(item) {
      const selfPrice = Number((item && item.product && item.product.self_price) || 0)
      const qty = Number((item && item.quantity) || 0)
      const revenue = this.itemLineTotal(item)
      const expenses = this.itemExpensesAmount(item) * qty
      const wastes = selfPrice * this.itemWastesQty(item) * qty
      let cost = 0
      if (this.profitParts.expenses) cost += expenses
      if (this.profitParts.wastes) cost += wastes
      return {
        revenue: Math.round(revenue * 100) / 100,
        expenses: Math.round(expenses * 100) / 100,
        wastes: Math.round(wastes * 100) / 100,
        profit: Math.round((revenue - cost) * 100) / 100,
      }
    },
    toggleProfitPart(key) {
      if (!Object.prototype.hasOwnProperty.call(this.profitParts, key)) return
      this.$set(this.profitParts, key, !this.profitParts[key])
      localStorage.setItem(PROFIT_PARTS_KEY, JSON.stringify(this.profitParts))
    },
    saleCost(sale) {
      return this.saleItems(sale).reduce((sum, item) => {
        const selfPrice = Number((item.product && item.product.self_price) || 0)
        const qty = Number((item && item.quantity) || 0)
        let line = 0
        if (this.profitParts.expenses) {
          line += this.itemExpensesAmount(item) * qty
        }
        if (this.profitParts.wastes) {
          line += selfPrice * this.itemWastesQty(item) * qty
        }
        return sum + line
      }, 0)
    },
    saleProfit(sale) {
      if (!sale || sale.status === 'refunded') return 0
      return Number(sale.total || 0) - this.saleCost(sale)
    },
    paymentTypeLabel(code) {
      if (!code) return '—'
      const found = this.paymentTypes.find((type) => type.value === code)
      return (found && found.label) || code
    },
    paymentLabel(sale) {
      const payments = this.salePayments(sale)
      if (payments.length > 1) return this.$t('sales.mixed')
      if (payments.length === 1) return this.paymentTypeLabel(payments[0].payment_type)
      return this.paymentTypeLabel(sale.payment_type)
    },
    resetToFirstPage() {
      if (this.page !== 1) {
        this.page = 1
      } else {
        this.load()
      }
    },
    debouncedSearch: debounce(function () {
      this.resetToFirstPage()
    }, 400),
    async loadPaymentTypes() {
      try {
        this.paymentTypes = await getPayments()
      } catch (error) {
        this.paymentTypes = []
      }
    },
    async loadProducts() {
      try {
        const list = []
        let page = 1
        let lastPage = 1
        do {
          const data = await getProducts({ page, per_page: 50, stock: 'stock' })
          list.push(...(data.data || []))
          lastPage = Number(data.last_page) || 1
          page += 1
        } while (page <= lastPage && page <= 10)

        this.products = list
          .slice()
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
      } catch (error) {
        this.products = []
      }
    },
    async loadSuppliers() {
      try {
        this.suppliers = await getSuppliers()
      } catch (error) {
        this.suppliers = []
      }
    },
    async load() {
      this.loading = true
      try {
        const data = await getSales(this.page, this.search, this.filterParams())
        this.sales = data.data || []
        this.pageCount = Number(data.last_page) || 0
        this.totalSales = data.total || 0

        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
        this.summary = await getSalesSummary(this.filterParams())
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    isPaymentTypeSelected(value) {
      return this.filters.payment_types.includes(value)
    },
    clearPaymentTypes() {
      this.filters.payment_types = []
    },
    togglePaymentType(value) {
      const selected = this.filters.payment_types
      const index = selected.indexOf(value)
      if (index === -1) {
        this.filters.payment_types = [...selected, value]
      } else {
        this.filters.payment_types = selected.filter((item) => item !== value)
      }
    },
    filterParams() {
      const params = {}
      Object.entries(this.filters).forEach(([key, value]) => {
        if (key === 'payment_types') {
          if (Array.isArray(value) && value.length) params.payment_types = value
          return
        }
        if (value === '' || value === false || value == null) return
        params[key] = value
      })
      return params
    },
    async exportCsv() {
      try {
        const file = await exportSales(this.filterParams())
        const url = URL.createObjectURL(file)
        const link = document.createElement('a')
        link.href = url
        link.download = `sales-${new Date().toISOString().slice(0, 10)}.csv`
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        notifyError(error)
      }
    },
    openRefund(sale) {
      if (!sale || sale.status === 'refunded') return
      this.refundReason = ''
      this.selectedSale = sale
      this.refundDialog = true
    },
    closeRefundDialog() {
      this.refundDialog = false
      this.selectedSale = null
      this.refundReason = ''
    },
    async refundSelected() {
      if (!this.selectedSale || this.selectedSale.status === 'refunded') return
      if (!this.refundReason.trim()) {
        notify(this.$t('sales.refundReasonRequired'), 'warning')
        return
      }
      try {
        await refundSale(this.selectedSale.id, this.refundReason.trim())
        notify(this.$t('sales.refundedMessage'))
        this.closeRefundDialog()
        await this.load()
      } catch (error) {
        notifyError(error)
      }
    },
    async printSale(sale) {
      const items = this.saleItems(sale).map((item) => ({
        id: item.id,
        name: this.itemName(item),
        quantity: item.quantity,
        unitPrice: this.itemUnitPrice(item),
        total: this.itemLineTotal(item),
        modifiers: this.itemModifiers(item).map((mod) => ({
          name: mod.name,
          price: Number(mod.price) || 0,
        })),
      }))
      const payload = {
        orderId: sale.id,
        createdAtLabel: this.formatDateTime(sale.created_at),
        company: getCompanyName(),
        cashier: this.cashierName(sale),
        isQuickSale: sale.sale_mode === 'quick',
        items,
        payments: this.salePayments(sale).map((pay) => ({
          type: pay.payment_type,
          label: this.paymentTypeLabel(pay.payment_type),
          amount: Number(pay.amount) || 0,
        })),
        total: Number(sale.total) || 0,
      }

      const printed = await tryPrintZywell(payload, { silent: true })
      if (printed) {
        notify('Чек отправлен на принтер')
        return
      }

      const popup = window.open('', '_blank', 'width=420,height=640')
      if (!popup) return
      const lines = items.map((item) => {
        const mods = (item.modifiers || []).map((m) => `  + ${m.name}`).join('<br>')
        return `<div><b>${item.name}</b><br>${mods}<div>${item.quantity} × ${this.formatMoney(item.unitPrice)} = <b>${this.formatMoney(item.total)}</b></div></div>`
      }).join('<hr>')
      popup.document.write(`<html><body style="font-family:Arial;padding:16px">
        <h2>${payload.company || ''} · чек №${sale.id}</h2>
        <p>${payload.createdAtLabel}</p>
        ${sale.sale_mode === 'quick' ? '<p>Быстрая продажа</p>' : lines}
        <h2>${this.formatMoney(sale.total)}</h2>
      </body></html>`)
      popup.document.close()
      popup.print()
    },
  },
}
</script>

<style scoped lang="scss">
.sales-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.sales-page__filters,
.sales-page__overview {
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  background: #fff;
}

.sales-page__filters {
  display: grid;
  gap: 12px;
}

.sales-page__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 180px;
  color: #6b7a90;
  font-weight: 700;

  .mdi {
    font-size: 28px;
    color: #0b4f8a;
  }
}

.sales-page__filter-row--dates {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(180px, 260px) auto;
  align-items: end;
  gap: 10px;
}

.sales-page__date-actions,
.sales-page__filter-extra {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.sales-page__filter-row--fields {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.1fr) minmax(0, 0.9fr) auto;
  align-items: start;
  gap: 10px;
}

.sales-page__select {
  width: 100%;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
  color: #152033;
  font-size: 14px;
}

.sales-page__date-field {
  display: grid;
  gap: 4px;
  min-width: min(220px, 100%);

  > span {
    color: #6b7a90;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.sales-page__date-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
  color: #152033;
  cursor: pointer;
  text-align: left;

  .mdi {
    color: #0b4f8a;
    font-size: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 650;
  }
}

.sales-page__today,
.sales-page__yesterday,
.sales-page__all-period,
.sales-page__clear-dates,
.sales-page__export,
.sales-page__check {
  min-height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
}

.sales-page__today,
.sales-page__yesterday,
.sales-page__all-period {
  border: 1px solid #0b4f8a;
  background: #fff;
  color: #0b4f8a;

  &.is-active {
    background: #0b4f8a;
    color: #fff;
  }
}

.sales-page__clear-dates {
  border: 1px solid #d9e2ec;
  background: #fff;
  color: #3d4f63;
}

.sales-page__check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d9e2ec;
  background: #f7fafc;
  color: #152033;
  font-size: 14px;
  white-space: nowrap;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #0b4f8a;
  }
}

.sales-page__export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: #0b4f8a;
  color: #fff;

  .mdi {
    font-size: 18px;
  }
}

.sales-date {
  padding: 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #d9e2ec;
  box-shadow: 0 18px 40px rgba(15, 40, 70, 0.16);
}

.sales-date__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 46px;
  margin-top: 10px;
  border: 0;
  border-radius: 14px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.sales-page__product-info {
  display: grid;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #f7fafc;
}

.sales-page__product-info-head {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;

  > div {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  span {
    color: #6b7a90;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    color: #152033;
    font-size: 16px;
    font-weight: 800;
    overflow-wrap: anywhere;
  }
}

.sales-page__pay-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid #e6eef5;
}

.sales-page__pay-label {
  color: #6b7a90;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-right: 2px;
}

.sales-page__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #fff;
  color: #152033;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;

  strong {
    font-weight: 800;
  }
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #9eb6cc;
  }

  &.is-active {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;
  }
}

.sales-page__overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
}

.sales-page__overview > div,
.sales-page__kpi-toggle {
  display: grid;
  gap: 4px;
  text-align: left;
}

.sales-page__overview span,
.sales-page__kpi-toggle span { color: #6b7a90; font-size: 12px; }
.sales-page__overview strong,
.sales-page__kpi-toggle strong { font-size: 18px; color: #152033; }

.sales-page__overview-hint {
  color: #8a97a8;
  font-size: 11px;
  font-style: normal;
  font-weight: 550;
  line-height: 1.3;
}

.sales-page__kpi-toggle {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 10px;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover {
    background: rgba(11, 79, 138, 0.04);
  }

  &.is-off {
    opacity: 0.42;
    text-decoration: line-through;

    strong {
      color: #8a97a8;
    }
  }
}

.sales-page__overview-profit strong {
  color: #0f7a4e;
}

.sales-page__formula {
  margin: -6px 0 16px;
  padding: 14px 16px;
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  background: #f7fafc;
}

.sales-page__formula-title {
  margin-bottom: 10px;
  color: #3d4f63;
  font-size: 13px;
  font-weight: 700;
}

.sales-page__formula-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
}

.sales-page__formula-term {
  display: grid;
  gap: 2px;
  min-width: 110px;
  padding: 8px 10px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e4ebf2;

  em {
    color: #6b7a90;
    font-size: 11px;
    font-style: normal;
    font-weight: 650;
  }

  strong {
    color: #152033;
    font-size: 15px;
    font-weight: 750;

    &.is-neg {
      color: #b42318;
    }
  }

  &--btn {
    cursor: pointer;
    text-align: left;
    transition: opacity 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: #0b4f8a;
    }
  }

  &.is-off {
    opacity: 0.4;
    text-decoration: line-through;
  }
}

.sales-page__formula-eq,
.sales-page__formula-op {
  color: #6b7a90;
  font-size: 18px;
  font-weight: 700;
}

.sales-page__formula-op.is-off {
  opacity: 0.35;
}

.sales-page__formula-hint {
  margin: 10px 0 0;
  color: #8a97a8;
  font-size: 12px;
  line-height: 1.4;
}

.sales-page__units {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;

  article {
    display: grid;
    gap: 6px;
    padding: 14px;
    border: 1px solid #d9e2ec;
    border-radius: 14px;
    background: #fff;
  }

  span {
    color: #6b7a90;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  strong {
    font-size: 18px;
    font-weight: 750;
    color: #152033;
    line-height: 1.3;
  }
}

.sale-card__badge--refunded { background: #fee4e2; color: #b42318; }
.sale-card__supplier {
  display: block;
  margin-top: 4px;
  color: #3d4f63;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
}

.sale-card__profit {
  display: block;
  margin-top: 4px;
  color: #0f7a4e;
  font-size: 13px;
  font-style: normal;
  font-weight: 650;

  &--neg {
    color: #b42318;
  }
}
.sale-detail-row { display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid #edf1f5; }
.sales-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.sales-page__header-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.sales-page__search {
  width: 320px;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
}

.sales-page__stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 88px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #d9e2ec;

  span {
    font-size: 12px;
    color: #6b7a90;
  }

  strong {
    font-size: 22px;
    font-weight: 750;
    color: #0b4f8a;
    line-height: 1.1;
  }
}

.sales-page__list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sale-card {
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #b7c6d6;
    box-shadow: 0 8px 24px rgba(16, 36, 58, 0.06);
  }
}

.sale-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-bottom: 1px solid #eef2f6;
}

.sale-card__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }

  time {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: #6b7a90;
  }
}

.sale-card__icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.sale-card__summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;

  strong {
    font-size: 20px;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #122033;
  }
}

.sale-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e8f1f8;
  color: #0b4f8a;
  font-size: 12px;
  font-weight: 650;

  &--discount {
    background: #e7f6ec;
    color: #1e7d3e;
  }

  &--quick {
    background: #fff4e5;
    color: #b45309;
  }
}

.sale-card__badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.sale-card__discount-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 7px;
  border-radius: 6px;
  background: #e7f6ec;
  color: #1e7d3e;
  font-size: 11px;
  font-weight: 650;
  font-style: normal;
  white-space: nowrap;
}

.sale-card__mods,
.sale-detail-mods,
.sale-detail-stock,
.sale-detail-together {
  display: block;
  margin-top: 4px;
  color: #6b7a90;
  font-size: 12px;
  font-weight: 500;
}

.sale-detail-stock {
  color: #0f7a4e;
  font-weight: 650;
}

.sale-card__together,
.sale-detail-together {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #eef6ff;
  color: #0b4f8a;
  font-size: 12px;
  font-weight: 650;
  white-space: normal;
  line-height: 1.35;

  .mdi {
    font-size: 14px;
  }

  em {
    font-style: normal;
    font-weight: 750;
  }
}

.sale-detail-together {
  display: block;
}

.sale-card__old-price {
  margin-right: 4px;
  color: #98a6b8;
  font-size: 12px;
}

.sale-card__cashier {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 12px;
  color: #6b7a90;

  .mdi {
    font-size: 14px;
  }
}

.sale-card__table {
  padding: 8px 10px 12px;
}

.sale-card__item {
  border-radius: 10px;
  padding: 2px 0 6px;

  &--alt {
    background: #f7f9fc;
  }
}

.sale-card__row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) 0.7fr 1fr 1fr;
  gap: 10px;
  align-items: center;
  padding: 10px 8px 4px;
  font-size: 14px;
  color: #3d4f63;
}

.sale-item__formula {
  margin: 0 8px 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f3f6fa;
  border: 1px solid #e4ebf2;
}

.sale-item__formula-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;

  .sale-item__formula-label {
    color: #6b7a90;
    font-size: 12px;
    font-weight: 650;
  }

  strong {
    color: #0f7a4e;
    font-size: 14px;
    font-weight: 750;

    &.is-neg {
      color: #b42318;
    }
  }
}

.sale-item__formula-parts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  color: #6b7a90;
  font-size: 12px;
  line-height: 1.35;

  .is-off {
    opacity: 0.4;
    text-decoration: line-through;
  }
}

.sale-item__formula-parts-op {
  color: #9aa8b8;
  font-weight: 700;
}

.sale-card__row--head {
  font-size: 12px;
  font-weight: 650;
  color: #6b7a90;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.sale-card__name {
  color: #152033;
  font-weight: 600;
  min-width: 0;
  white-space: normal;
}

.sale-card__line {
  font-weight: 700;
  color: #152033;
}

.sale-card__empty-items {
  padding: 18px;
  color: #6b7a90;
  font-size: 14px;

  &--quick {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0b4f8a;

    .mdi {
      font-size: 18px;
    }
  }
}

.sale-card__payments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 18px 12px;
}

.sale-card__pay-chip {
  padding: 5px 10px;
  border-radius: 8px;
  background: #f3f6fa;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  color: #3d4f63;
}

.sale-card__actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 18px 16px;
}

.sale-card__print-btn,
.sale-card__refund-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  .mdi {
    font-size: 18px;
  }
}

.sale-card__print-btn {
  border: 1px solid #d9e2ec;
  background: #fff;
  color: #0b4f8a;

  &:hover {
    background: #f3f7fb;
    border-color: #0b4f8a;
  }
}

.sale-card__refund-btn {
  border: 1px solid #fecdca;
  background: #fff;
  color: #b42318;

  &:hover {
    background: #fff1f0;
    border-color: #f97066;
  }
}

.sale-refund-modal {
  padding: 18px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #d9e2ec;
  box-shadow: 0 18px 40px rgba(15, 40, 70, 0.16);
}

.sale-refund-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 780;
  }

  p {
    margin: 4px 0 0;
    color: #6b7a90;
    font-size: 13px;
  }
}

.sale-refund-modal__close {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f3f6fa;
  color: #3d4f63;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.sale-refund-modal__field {
  display: grid;
  gap: 6px;

  span {
    color: #6b7a90;
    font-size: 12px;
    font-weight: 700;
  }

  textarea {
    width: 100%;
    min-height: 88px;
    padding: 12px;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    resize: vertical;
    font: inherit;
    color: #152033;
    outline: none;

    &:focus {
      border-color: #0b4f8a;
    }
  }
}

.sale-refund-modal__footer {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.sale-refund-modal__btn {
  flex: 1;
  min-height: 44px;
  border: 0;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;

  &--ghost {
    background: #f3f6fa;
    color: #3d4f63;
  }

  &--danger {
    background: #b42318;
    color: #fff;
  }
}

.sales-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.sales-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

@media (max-width: 1100px) {
  .sales-page__filter-row--dates,
  .sales-page__filter-row--fields {
    grid-template-columns: 1fr 1fr;
  }

  .sales-page__date-actions,
  .sales-page__filter-extra {
    grid-column: 1 / -1;
  }
}

@media (max-width: 720px) {
  .sales-page {
    padding: 20px 16px 32px;
  }

  .sales-page__product-info-head {
    grid-template-columns: 1fr;
  }

  .sales-page__filter-row--dates,
  .sales-page__filter-row--fields {
    grid-template-columns: 1fr;
  }

  .sales-page__date-field {
    min-width: 0;
    width: 100%;
  }

  .sales-page__date-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .sales-page__date-actions > *,
  .sales-page__filter-extra,
  .sales-page__check,
  .sales-page__export {
    width: 100%;
  }

  .sales-page__filter-extra {
    display: grid;
    grid-template-columns: 1fr;
  }

  .sales-page__check {
    justify-content: flex-start;
  }

  .sales-page__header {
    flex-direction: column;
  }

  .sales-page__header-tools {
    width: 100%;
    justify-content: stretch;
  }

  .sales-page__search {
    width: 100%;
  }

  .sales-page__stat {
    align-items: flex-start;
  }

  .sale-card__head {
    flex-direction: column;
    align-items: flex-start;
  }

  .sales-page__overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sales-page__units {
    grid-template-columns: 1fr;
  }

  .sale-card__summary {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .sale-card__row,
  .sale-card__row--head {
    grid-template-columns: 1.4fr 0.7fr 0.9fr 1fr;
  }
}
</style>

<style lang="scss">
.sales-filter-date {
  box-shadow: none !important;
  background: transparent !important;
}
</style>
